import {computed, effect, inject, Injectable, isDevMode, signal} from '@angular/core';
import {endpoints} from '../endpoints';
import {BaseResponseApiRoot, UserMeRes} from '../../models/user.model';
import {firstValueFrom} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {AuthService} from '../auth/auth-service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  /**
   * Service per caricare e mantenere i dati dell'utente
   */

  private http = inject(HttpClient);
  private authService = inject(AuthService);

  currentUser = signal<UserMeRes | null>(null);

  /**
   * Richiesta a `/users/me` attualmente in volo, condivisa fra i chiamanti
   * concorrenti. Serve perché al refresh partono due trigger distinti e
   * legittimi — il `UserGuard` ("sto cambiando pagina") e l'effect di
   * `HeaderGlobal` ("è cambiato chi sono") — che senza questo si trovano
   * entrambi `currentUser()` ancora null e aprono due GET identiche.
   */
  private inFlightRequest: Promise<void> | null = null;

  constructor() {
    effect(() => {
      if (!this.authService.isAuthenticated()) {
        this.currentUser.set(null);
      }
    });
  }

  // Un signal direttamente calcolato da currentUser così è sempre aggiornato
  //TODO quando ci sarà l'impersonificazione dovrà essere messa anche quella condizione
  currentUserPermission = computed(() => this.currentUser()?.userType);

  private getUserInfo() {
    const url = endpoints.userInfoEndpoint;
    return this.http.get<BaseResponseApiRoot<UserMeRes>>(url);
  }

  /**
   * Questo metodo viene chiamato
   * per caricare i dati dell'utente.
   */
  async fetchUserInfo(force: boolean = false): Promise<void> {
    // Non fare nulla se l'utente non è autenticato o se i dati sono già stati caricati
    if (!this.authService.isAuthenticated() || (!force && this.currentUser() !== null)) {
      return;
    }

    // Se una richiesta è già partita ci si aggancia, invece di aprirne una seconda.
    // Chi passa `force` non si aggancia mai: forza il ricaricamento dopo aver
    // modificato i dati (primo accesso, salvataggio anagrafica) e una richiesta
    // partita prima della modifica restituirebbe valori ormai vecchi.
    if (!force && this.inFlightRequest) {
      return this.inFlightRequest;
    }

    const request = this.loadUserInfo().finally(() => {
      // Solo se nel frattempo non è subentrata una richiesta più recente
      // (es. una `force` che ha sostituito questa).
      if (this.inFlightRequest === request) {
        this.inFlightRequest = null;
      }
    });

    // Anche le `force` si registrano: un chiamante non forzato che arriva ora
    // si aggancia a questa e ottiene comunque il dato aggiornato.
    this.inFlightRequest = request;

    return request;
  }

  /**
   * Esecuzione effettiva della GET. Non rilancia mai: l'errore viene assorbito
   * qui (logout + pulizia) esattamente come prima, così tutti i chiamanti
   * agganciati alla stessa promise si risolvono normalmente e nessuno di loro
   * si ritrova un rejection inatteso da gestire.
   */
  private async loadUserInfo(): Promise<void> {
    try {
      const userInfo = await firstValueFrom(this.getUserInfo());
      this.currentUser.set(userInfo.res);
    } catch (error) {
      if (isDevMode()) console.error('Failed to fetch user info', error);
      this.authService.logout();
      this.clearUserInfo();
    }
  }

  // Metodo per pulire i dati dell'utente al logout
  clearUserInfo(): void {
    this.currentUser.set(null);
  }

}
